/**
 * QueueSettingsPanel — UI for editing the autonomous ticket queue config
 * for the active project. Lives at .ateliercode/config.json under the
 * `work` block. Each of the four roles (planner / worker / validator /
 * tester) gets a dropdown; the registry of available agents is shown
 * read-only here for V1 (edit by hand in config.json or via a future
 * AgentRegistry editor).
 *
 * Persists via backend.files.writeAteliercodeConfig — same path the
 * shell scripts read. Edits here take effect on the NEXT control script
 * invocation, no app restart needed.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bot, Save, RotateCcw, AlertCircle, Gauge } from 'lucide-react';
import { useBackend } from '@/services/backend';
import {
  DEFAULT_WORK_QUEUE,
  type AteliercodeConfig,
  type QueueRole,
  type WorkQueueConfig,
} from '@/lib/projectSettings';
import { logger } from '@/lib/logger';

const TAG = 'QueueSettingsPanel';

const ROLES: { id: QueueRole; label: string; help: string }[] = [
  { id: 'planner',   label: 'Planner',   help: 'Breaks user intent into tickets in todo/.' },
  { id: 'worker',    label: 'Worker',    help: 'Executes one ticket at a time, moves it to review/.' },
  { id: 'validator', label: 'Validator', help: 'Strictly judges tickets in review/, routes to done/ / fix/ / escalate/.' },
  { id: 'tester',    label: 'Tester',    help: 'Runs the project test suite, reports PASS/FAIL.' },
];

interface QueueSettingsPanelProps {
  projectId: string;
}

export default function QueueSettingsPanel({ projectId }: QueueSettingsPanelProps) {
  const backend = useBackend();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<'idle' | 'saving' | 'success'>('idle');
  const [config, setConfig] = useState<AteliercodeConfig | null>(null);
  const [work, setWork] = useState<WorkQueueConfig>(DEFAULT_WORK_QUEUE);
  const [dirty, setDirty] = useState(false);

  // Load on mount + on project switch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const raw = await backend.files.readAteliercodeConfig(projectId);
        if (cancelled) return;
        const parsed: AteliercodeConfig = raw ? JSON.parse(raw) : {};
        setConfig(parsed);
        setWork({
          path: parsed.work?.path ?? DEFAULT_WORK_QUEUE.path,
          maxAttempts: parsed.work?.maxAttempts ?? DEFAULT_WORK_QUEUE.maxAttempts,
          maxConcurrent: parsed.work?.maxConcurrent ?? DEFAULT_WORK_QUEUE.maxConcurrent,
          roles: { ...DEFAULT_WORK_QUEUE.roles, ...(parsed.work?.roles ?? {}) } as Record<QueueRole, string>,
          agents: { ...DEFAULT_WORK_QUEUE.agents, ...(parsed.work?.agents ?? {}) },
        });
        setDirty(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, backend]);

  const setRole = useCallback((role: QueueRole, agentId: string) => {
    setWork((prev) => ({ ...prev, roles: { ...prev.roles, [role]: agentId } }));
    setDirty(true);
    setSaved('idle');
  }, []);

  const setMaxAttempts = useCallback((n: number) => {
    setWork((prev) => ({ ...prev, maxAttempts: Math.max(1, Math.min(20, n)) }));
    setDirty(true);
    setSaved('idle');
  }, []);

  const setMaxConcurrent = useCallback((n: number) => {
    setWork((prev) => ({ ...prev, maxConcurrent: Math.max(1, Math.min(16, n)) }));
    setDirty(true);
    setSaved('idle');
  }, []);

  const setPath = useCallback((p: string) => {
    setWork((prev) => ({ ...prev, path: p }));
    setDirty(true);
    setSaved('idle');
  }, []);

  const save = useCallback(async () => {
    setSaved('saving');
    try {
      const next: AteliercodeConfig = { ...(config ?? {}), work };
      await backend.files.writeAteliercodeConfig(projectId, JSON.stringify(next, null, 2));
      setConfig(next);
      setDirty(false);
      setSaved('success');
      setTimeout(() => setSaved('idle'), 2000);
    } catch (e) {
      logger.warn(TAG, 'save failed', e);
      setError(e instanceof Error ? e.message : String(e));
      setSaved('idle');
    }
  }, [backend, projectId, work, config]);

  const reset = useCallback(() => {
    setWork({
      path: config?.work?.path ?? DEFAULT_WORK_QUEUE.path,
      maxAttempts: config?.work?.maxAttempts ?? DEFAULT_WORK_QUEUE.maxAttempts,
      maxConcurrent: config?.work?.maxConcurrent ?? DEFAULT_WORK_QUEUE.maxConcurrent,
      roles: { ...DEFAULT_WORK_QUEUE.roles, ...(config?.work?.roles ?? {}) } as Record<QueueRole, string>,
      agents: { ...DEFAULT_WORK_QUEUE.agents, ...(config?.work?.agents ?? {}) },
    });
    setDirty(false);
    setSaved('idle');
  }, [config]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><span className="loading loading-spinner loading-md" /></div>;
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  const agentIds = Object.keys(work.agents);

  return (
    <div className="space-y-6">
      {/* Roles */}
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-4">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" />
            <h4 className="font-medium">Role assignments</h4>
          </div>
          <p className="text-xs text-base-content/60">
            Choose which agent plays each role in the autonomous queue. The
            control scripts (<code>scripts/queue/*</code>) read this on every
            invocation, so changes take effect immediately.
          </p>

          {ROLES.map((r) => (
            <div key={r.id} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
              <div>
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-xs text-base-content/50">{r.help}</div>
              </div>
              <div className="sm:col-span-2">
                <select
                  value={work.roles[r.id]}
                  onChange={(e) => setRole(r.id, e.target.value)}
                  className="select select-bordered select-sm w-full"
                >
                  {agentIds.map((id) => (
                    <option key={id} value={id}>
                      {work.agents[id]?.displayName || id} ({id})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Agent registry (read-only V1) */}
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" />
            <h4 className="font-medium">Agent registry</h4>
          </div>
          <p className="text-xs text-base-content/60">
            Available agents the roles above can point at. Edit{' '}
            <code>.ateliercode/config.json</code> directly to add or remove
            agents — a UI editor is on the roadmap.
          </p>
          <div className="overflow-x-auto">
            <table className="table table-xs">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Display name</th>
                  <th>Command</th>
                  <th>Args</th>
                </tr>
              </thead>
              <tbody>
                {agentIds.map((id) => {
                  const a = work.agents[id];
                  return (
                    <tr key={id}>
                      <td className="font-mono text-xs">{id}</td>
                      <td>{a.displayName}</td>
                      <td className="font-mono text-xs">{a.command}</td>
                      <td className="font-mono text-xs">{(a.args ?? []).join(' ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Concurrency throttle */}
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-primary" />
            <h4 className="font-medium">Concurrency</h4>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="queue-max-concurrent">
              Max concurrent local workers
            </label>
            <p className="text-xs text-base-content/50 mt-0.5 mb-2">
              Dial down when running multiple projects so they share capacity.
              Each worker is a full local-model inference, so more isn&apos;t
              always faster.
            </p>
            <div className="flex items-center gap-3">
              <input
                id="queue-max-concurrent"
                type="range"
                min={1}
                max={16}
                step={1}
                value={work.maxConcurrent ?? 3}
                onChange={(e) => setMaxConcurrent(parseInt(e.target.value, 10))}
                className="range range-primary range-sm flex-1"
              />
              <input
                type="number"
                value={work.maxConcurrent ?? 3}
                onChange={(e) => setMaxConcurrent(parseInt(e.target.value || '3', 10))}
                min={1}
                max={16}
                className="input input-bordered input-sm w-20 font-mono"
                aria-label="Max concurrent local workers"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Queue path + circuit breaker */}
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-4">
          <div>
            <label className="text-sm font-medium">Queue path (relative to project root)</label>
            <input
              type="text"
              value={work.path}
              onChange={(e) => setPath(e.target.value)}
              className="input input-bordered input-sm w-full mt-1 font-mono"
              placeholder=".ateliercode/work"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Max attempts (circuit breaker)</label>
            <input
              type="number"
              value={work.maxAttempts ?? 3}
              onChange={(e) => setMaxAttempts(parseInt(e.target.value || '3', 10))}
              min={1}
              max={20}
              className="input input-bordered input-sm w-full mt-1 font-mono"
            />
            <p className="text-xs text-base-content/50 mt-1">
              After this many failed verification attempts, the ticket moves
              to <code>escalate/</code> instead of being retried.
            </p>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="flex items-center gap-2 sticky bottom-0 bg-base-100 py-2">
        <button
          type="button"
          className="btn btn-primary btn-sm gap-2"
          onClick={save}
          disabled={!dirty || saved === 'saving'}
        >
          <Save className="w-4 h-4" />
          {saved === 'saving' ? 'Saving…' : saved === 'success' ? 'Saved ✓' : 'Save changes'}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm gap-2"
          onClick={reset}
          disabled={!dirty}
        >
          <RotateCcw className="w-4 h-4" /> Discard
        </button>
        {dirty && <span className="text-xs text-warning">Unsaved changes</span>}
      </div>
    </div>
  );
}
